import React, { useEffect, useRef, useState } from "react";
import { UploadZone, DownloadButton, ResetButton, loadImage } from "./_shared";
import { Slider } from "@/components/ui/slider";
import { Type, Image as ImageIcon, Upload as UploadIcon } from "lucide-react";

const TABS = { text: "Text", image: "Logo/Image" };

export default function WatermarkImage() {
  const [file, setFile] = useState(null);
  const [src, setSrc] = useState(null);
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 });

  const [mode, setMode] = useState("text"); // text | image
  const [text, setText] = useState("© ImageKitHub");
  const [color, setColor] = useState("#ffffff");
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoImg, setLogoImg] = useState(null);
  const [tint, setTint] = useState("#ffffff");
  const [useTint, setUseTint] = useState(false);

  // Watermark transform (percent for position, percent width for size, degrees rotation)
  const [pos, setPos] = useState({ x: 50, y: 85 });
  const [scale, setScale] = useState(25); // width % of base image
  const [rot, setRot] = useState(0);
  const [opacity, setOpacity] = useState(0.6);

  const [outUrl, setOutUrl] = useState(null);
  const [dragMode, setDragMode] = useState(null); // "move" | "resize" | "rotate"
  const dragStart = useRef(null);
  const previewRef = useRef(null);
  const logoInputRef = useRef(null);

  const handleFile = async (f) => {
    const { img } = await loadImage(f);
    setFile(f); setSrc(URL.createObjectURL(f));
    setImgDims({ w: img.naturalWidth, h: img.naturalHeight });
  };

  const handleLogo = async (f) => {
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => { setLogoImg(img); };
    img.src = url;
    setLogoFile(f); setLogoUrl(url);
  };

  const getEventXY = (e) => e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };

  const startDrag = (e, kind) => {
    e.preventDefault(); e.stopPropagation();
    const { x, y } = getEventXY(e);
    dragStart.current = { x, y, pos: { ...pos }, scale, rot };
    setDragMode(kind);
  };
  const onMove = (e) => {
    if (!dragMode || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    const { x, y } = getEventXY(e);
    const dxPct = ((x - dragStart.current.x) / rect.width) * 100;
    const dyPct = ((y - dragStart.current.y) / rect.height) * 100;
    if (dragMode === "move") {
      setPos({
        x: Math.max(0, Math.min(100, dragStart.current.pos.x + dxPct)),
        y: Math.max(0, Math.min(100, dragStart.current.pos.y + dyPct)),
      });
    } else if (dragMode === "resize") {
      const delta = Math.max(dxPct, dyPct);
      setScale(Math.max(5, Math.min(100, dragStart.current.scale + delta)));
    } else if (dragMode === "rotate") {
      const cx = rect.left + rect.width * (pos.x / 100);
      const cy = rect.top + rect.height * (pos.y / 100);
      const angle = Math.atan2(y - cy, x - cx) * (180 / Math.PI);
      setRot(angle + 90); // handle above center = 0deg
    }
  };
  const endDrag = () => setDragMode(null);

  useEffect(() => {
    if (!dragMode) return;
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", endDrag);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", endDrag);
    };
  }, [dragMode]); // eslint-disable-line

  const process = async () => {
    if (!file) return;
    const { img } = await loadImage(file);
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.translate((pos.x / 100) * canvas.width, (pos.y / 100) * canvas.height);
    ctx.rotate((rot * Math.PI) / 180);
    if (mode === "text") {
      const fontSize = (scale / 100) * canvas.width * 0.35; // approx
      ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`;
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.shadowColor = "rgba(0,0,0,0.5)"; ctx.shadowBlur = 4;
      ctx.fillText(text, 0, 0);
    } else if (mode === "image" && logoImg) {
      const w = (scale / 100) * canvas.width;
      const h = (logoImg.naturalHeight / logoImg.naturalWidth) * w;
      if (useTint) {
        const tmp = document.createElement("canvas");
        tmp.width = w; tmp.height = h;
        const tctx = tmp.getContext("2d");
        tctx.drawImage(logoImg, 0, 0, w, h);
        tctx.globalCompositeOperation = "source-in";
        tctx.fillStyle = tint;
        tctx.fillRect(0, 0, w, h);
        ctx.drawImage(tmp, -w / 2, -h / 2);
      } else {
        ctx.drawImage(logoImg, -w / 2, -h / 2, w, h);
      }
    }
    ctx.restore();
    canvas.toBlob((blob) => {
      if (outUrl) URL.revokeObjectURL(outUrl);
      setOutUrl(URL.createObjectURL(blob));
    }, "image/png", 0.95);
  };

  const reset = () => {
    setFile(null); setSrc(null); setOutUrl(null);
    setPos({ x: 50, y: 85 }); setScale(25); setRot(0);
  };

  if (!file) return <UploadZone onFile={handleFile} />;

  // Preview overlay style
  const overlayStyle = {
    left: `${pos.x}%`, top: `${pos.y}%`,
    transform: `translate(-50%, -50%) rotate(${rot}deg)`,
    opacity, width: `${scale}%`,
  };

  return (
    <div className="space-y-6">
      {/* Mode tabs */}
      <div className="inline-flex p-1 rounded-full bg-slate-100 dark:bg-white/5">
        {Object.entries(TABS).map(([k, label]) => {
          const Icon = k === "text" ? Type : ImageIcon;
          return (
            <button
              key={k}
              onClick={() => setMode(k)}
              data-testid={`watermark-mode-${k}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-colors ${mode === k ? "bg-white dark:bg-[#1a1c20] text-blue-600 shadow-sm" : "text-slate-600 dark:text-slate-300"}`}
            >
              <Icon size={14} /> {label}
            </button>
          );
        })}
      </div>

      {/* Preview */}
      <div
        ref={previewRef}
        className="relative rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden bg-slate-50 dark:bg-[#0b0c0e] select-none"
        style={{ aspectRatio: imgDims.w && imgDims.h ? `${imgDims.w} / ${imgDims.h}` : "16 / 9", maxHeight: 500 }}
        data-testid="watermark-preview"
      >
        <img src={src} alt="src" className="w-full h-full object-contain pointer-events-none" draggable={false} />
        <div
          onMouseDown={(e) => startDrag(e, "move")}
          onTouchStart={(e) => startDrag(e, "move")}
          className="absolute cursor-move"
          style={overlayStyle}
        >
          <div className="relative">
            {mode === "text" ? (
              <div
                className="font-bold whitespace-nowrap text-center"
                style={{ color, fontSize: `${scale / 3}px`, textShadow: "0 2px 4px rgba(0,0,0,0.5)", lineHeight: 1 }}
              >
                {text}
              </div>
            ) : logoUrl ? (
              <img
                src={logoUrl}
                alt="logo"
                className="w-full h-auto pointer-events-none"
                style={useTint ? {
                  filter: "brightness(0) saturate(100%)",
                  WebkitMaskImage: `url(${logoUrl})`,
                  maskImage: `url(${logoUrl})`,
                  WebkitMaskSize: "contain",
                  WebkitMaskRepeat: "no-repeat",
                  background: tint,
                } : undefined}
                draggable={false}
              />
            ) : (
              <div className="text-xs px-3 py-2 bg-white/80 dark:bg-black/60 text-slate-700 dark:text-white rounded-md">Upload a logo →</div>
            )}
            {/* Rotate handle */}
            <div
              onMouseDown={(e) => startDrag(e, "rotate")}
              onTouchStart={(e) => startDrag(e, "rotate")}
              className="absolute left-1/2 -translate-x-1/2 -top-6 w-3 h-3 rounded-full bg-blue-600 border-2 border-white cursor-grab shadow"
              data-testid="watermark-rotate-handle"
            />
            {/* Resize handle */}
            <div
              onMouseDown={(e) => startDrag(e, "resize")}
              onTouchStart={(e) => startDrag(e, "resize")}
              className="absolute -right-1.5 -bottom-1.5 w-3 h-3 rounded-sm bg-blue-600 border-2 border-white cursor-nwse-resize shadow"
              data-testid="watermark-resize-handle"
            />
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Drag to move · corner handle to resize · top handle to rotate. Live preview.
      </p>

      {/* Controls */}
      <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-6 bg-white dark:bg-[#121316] space-y-5">
        {mode === "text" ? (
          <>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1 block">Watermark text</label>
              <input
                value={text} onChange={(e) => setText(e.target.value)} data-testid="watermark-text"
                className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#1a1c20] px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Text color</label>
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="watermark-color" className="w-10 h-10 rounded-lg border border-slate-200 dark:border-white/10 cursor-pointer bg-transparent" />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1 block">Logo image</label>
              <input ref={logoInputRef} type="file" accept="image/*" onChange={(e) => e.target.files[0] && handleLogo(e.target.files[0])} className="hidden" data-testid="watermark-logo-input" />
              <button onClick={() => logoInputRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-white/10 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                <UploadIcon size={14} /> {logoFile ? logoFile.name.slice(0, 24) : "Choose logo"}
              </button>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={useTint} onChange={(e) => setUseTint(e.target.checked)} data-testid="watermark-tint-toggle" /> Tint logo
              </label>
              <input
                type="color"
                value={tint}
                onChange={(e) => setTint(e.target.value)}
                data-testid="watermark-tint-color"
                className={`w-10 h-10 rounded-lg border border-slate-200 dark:border-white/10 cursor-pointer bg-transparent ${useTint ? "" : "invisible"}`}
              />
            </div>
          </>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <SliderRow label="Size" value={scale} min={5} max={100} step={1} onChange={setScale} suffix="%" testid="watermark-size" />
          <SliderRow label="Rotation" value={Math.round(rot)} min={-180} max={180} step={1} onChange={setRot} suffix="°" testid="watermark-rotation" />
          <SliderRow label="Opacity" value={Math.round(opacity * 100)} min={5} max={100} step={5} onChange={(v) => setOpacity(v / 100)} suffix="%" testid="watermark-opacity" />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={process} data-testid="tool-process-button" className="px-5 py-2.5 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium hover:opacity-90 transition-opacity">
          Apply watermark
        </button>
        <DownloadButton href={outUrl} filename={`watermark-${file.name}`}>Download</DownloadButton>
        <ResetButton onClick={reset} />
      </div>

      {outUrl && (
        <div>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Result</p>
          <img src={outUrl} alt="watermarked" className="max-h-96 rounded-xl border border-slate-200 dark:border-white/10" />
        </div>
      )}
    </div>
  );
}

function SliderRow({ label, value, min, max, step, onChange, suffix, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">{label}</label>
        <span className="text-sm text-slate-500 dark:text-slate-400">{value}{suffix}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} data-testid={testid} />
    </div>
  );
}
